import {CdkDrag, CdkDropList} from '@angular/cdk/drag-drop';
import {afterNextRender, Directive, inject, Injector, input, output} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

export type ReorderableRowMove = {from: number; to: number};

/**
 * `grip`: bare ArrowUp/ArrowDown on the grip. `row`: Alt/Option+ArrowUp/ArrowDown anywhere in the row,
 * for rows whose own fields need the bare arrows.
 */
export type ReorderableRowKeyboard = 'grip' | 'row';

@Directive({
    selector: '[appReorderableRows]',
    standalone: true,
    hostDirectives: [CdkDropList],
    host: {class: 'app-reorderable-rows'},
})
export class ReorderableRowsDirective {
    readonly keyboard = input<ReorderableRowKeyboard>('grip', {
        alias: 'appReorderableRowsKeyboard',
    });

    readonly rowMoved = output<ReorderableRowMove>();

    private readonly dropList = inject(CdkDropList);
    private readonly injector = inject(Injector);

    constructor() {
        this.dropList.dropped
            .pipe(takeUntilDestroyed())
            .subscribe(drop =>
                this.rowMoved.emit({from: drop.previousIndex, to: drop.currentIndex}),
            );
    }

    // Moving a row detaches its DOM, which drops focus, so the element the key came from is refocused.
    moveBy(drag: CdkDrag, offset: number, origin: EventTarget | null): void {
        const rows = this.dropList.getSortedItems();
        const from = rows.indexOf(drag);
        const to = from + offset;
        if (to < 0 || to >= rows.length) {
            return;
        }
        this.rowMoved.emit({from, to});
        if (origin instanceof HTMLElement) {
            afterNextRender(() => origin.focus(), {injector: this.injector});
        }
    }
}
